import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../auth/api';
import { useAuth } from '../auth/AuthProvider';
import { AuthCard, Button, FieldLabel, FormError, TextInput } from '../components/FormPrimitives';

export default function ChangePassword() {
  const { user, patchUser } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isForced = user?.must_change_password === true;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    if (next.length < 8 || !/[A-Z]/.test(next) || !/[0-9]/.test(next)) {
      setError('New password must be at least 8 characters and include an uppercase letter and a number.');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(current, next);
      patchUser({ must_change_password: false });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password change failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title={isForced ? 'Set a new password' : 'Change password'}
      subtitle={
        isForced
          ? 'Your account requires a password change before continuing.'
          : 'Choose a new password for your account.'
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <FormError message={error} />

        <div style={{ marginBottom: 16 }}>
          <FieldLabel htmlFor="current">Current password</FieldLabel>
          <TextInput
            id="current"
            name="current_password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.currentTarget.value)}
            disabled={submitting}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <FieldLabel htmlFor="new">New password</FieldLabel>
          <TextInput
            id="new"
            name="new_password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.currentTarget.value)}
            disabled={submitting}
          />
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            8+ characters, with at least one uppercase letter and one number.
          </p>
        </div>

        <div style={{ marginBottom: 24 }}>
          <FieldLabel htmlFor="confirm">Confirm new password</FieldLabel>
          <TextInput
            id="confirm"
            name="confirm_password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            disabled={submitting}
          />
        </div>

        <Button type="submit" disabled={submitting} style={{ width: '100%' }}>
          {submitting ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </AuthCard>
  );
}
